import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBlogBySlug, resolveAssetUrl } from '../api/api';
import Footer from '../components/Footer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const categoryColors = {
  technical: '#0f3460',
  news: '#e8920c',
  event: '#198754',
  announcement: '#dc3545',
  general: '#6c757d',
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getBlogBySlug(slug)
      .then((res) => setBlog(res.data))
      .catch((err) => {
        if (err.response?.status === 404) navigate('/blog');
        else setError('Failed to load post');
      })
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner-border text-warning" role="status" />
      </div>
    );
  }

  if (error) {
    return <div className="container py-5 text-center text-danger">{error}</div>;
  }

  if (!blog) return null;

  const color = categoryColors[blog.category] || '#6c757d';

  const categoryLabel = {
    technical: t('Home.Blog.Technical'),
    news: t('Home.Blog.News'),
    event: t('Home.Blog.Event'),
    announcement: t('Home.Blog.Announcement'),
    general: t('Home.Blog.General'),
  }[blog.category] || blog.category;

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <div
        style={{
          background: `linear-gradient(135deg, #1a1a2e 0%, ${color} 100%)`,
          padding: '100px 0 60px',
          color: 'white',
        }}
      >
        <div className="container" style={{ maxWidth: '780px' }}>
          <Link
            to="/blog"
            style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '14px' }}
          >
            {t('Home.Blog.BackToBlog')}
          </Link>
          <div style={{ marginTop: '20px' }}>
            <span
              style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '4px 14px',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {categoryLabel}
            </span>
          </div>
          <h1 style={{ fontWeight: 700, fontSize: '2.2rem', marginTop: '16px', lineHeight: 1.3 }}>
            {blog.title}
          </h1>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginTop: '20px',
              fontSize: '14px',
              opacity: 0.8,
              flexDirection: isRTL ? 'row-reverse' : 'row',
              justifyContent: isRTL ? 'flex-end' : 'flex-start',
            }}
          >
            <span>
              <i className={`bi bi-person-circle ${isRTL ? 'ms-1' : 'me-1'}`} />
              {t('Home.Blog.By')} {blog.author?.fullName || 'ENGPI'}
            </span>
            <span>
              <i className={`bi bi-calendar3 ${isRTL ? 'ms-1' : 'me-1'}`} />
              {new Date(blog.createdAt).toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Cover Image */}
      {blog.coverImage && (
        <div className="container" style={{ maxWidth: '780px', marginTop: '-30px' }}>
          <img
            src={resolveAssetUrl(blog.coverImage)}
            alt={blog.title}
            style={{
              width: '100%',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              maxHeight: '400px',
              objectFit: 'cover',
            }}
          />
        </div>
      )}

      {/* Content */}
      <div className="container py-5" style={{ maxWidth: '780px' }}>
        <div
          className="blog-markdown-body"
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            lineHeight: 1.8,
            fontSize: '16px',
            color: '#333',
            textAlign: isRTL ? 'right' : 'left',
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{blog.content}</ReactMarkdown>
        </div>
      </div>

      <Footer />
    </div>
  );
}
